// Logger utility - provides safe logging function for product pages and other scripts
// Defined early to ensure availability before any other scripts execute
window.logger = window.logger || function(...args) {
  if (window.console && typeof window.console.log === 'function') {
    window.console.log(...args);
  }
};

/**
 * Restore vet view after feedback form submit.
 *
 * Shopify redirects contact form POSTs to `/?contact_posted=true#...` which drops the original URL.
 * - Pages with ?view= param: restore same view (e.g. vet-home, jcsb-b2b). Source: sessionStorage or referrer.
 * - Pages without view param (e.g. /pages/educational-videos): restore path. Source: sessionStorage (path stored on submit).
 */
(function restoreVetViewAfterFeedbackSubmit() {
  const VET_VIEW_PARAMS = [
    'vet-home',
    'vet-portal-shop-all',
    'dcsc50-b2b',
    'dcsc50-b2c',
    'jcsb-b2b',
    'jcsb-b2c',
    'jclb-b2b',
    'jclb-b2c',
    'jlucii-b2b',
    'jlucii-b2c',
    'mv2330-b2b',
    'mv2330-b2c',
    'pbc2b-b2b',
    'pbc2b-b2c',
    'laser-b2b',
    'laser-b2c',
    'wapo08-b2b',
    'wapo08-b2c',
    'wapo16-b2b',
    'wapo16-b2c',
  ];

  function isAllowedReturnPath(pathname) {
    if (!pathname || pathname.indexOf('..') !== -1) return false;
    return pathname === '/' || pathname.indexOf('/pages/') === 0;
  }

  function getViewFromReferrer() {
    if (!document.referrer) return null;
    try {
      const ref = new URL(document.referrer);
      const v = ref.searchParams.get('view');
      return VET_VIEW_PARAMS.includes(v) ? v : null;
    } catch (e) {
      return null;
    }
  }

  try {
    const url = new URL(window.location.href);
    const hasContactPosted = url.searchParams.get('contact_posted') === 'true';
    if (!hasContactPosted) return;

    const hasFeedbackHash = /FeedbackForm-/.test(url.hash || '');
    if (!hasFeedbackHash) return;

    const sessionStorage = window.sessionStorage;
    const storedPath = sessionStorage ? sessionStorage.getItem('wuffesFeedbackReturnPath') : null;

    if (storedPath && isAllowedReturnPath(storedPath)) {
      try {
        if (sessionStorage) sessionStorage.removeItem('wuffesFeedbackReturnPath');
      } catch (e) {}
      const returnUrl = storedPath + '?contact_posted=true' + (url.hash || '');
      if (returnUrl !== url.pathname + url.search + (url.hash || '')) {
        window.location.replace(returnUrl);
      }
      return;
    }

    const storedView = sessionStorage ? sessionStorage.getItem('wuffesFeedbackReturnView') : null;
    let intendedView =
      (storedView && VET_VIEW_PARAMS.includes(storedView)) ? storedView : getViewFromReferrer();

    if (!intendedView) {
      if (sessionStorage) sessionStorage.removeItem('wuffesFeedbackReturnView');
      return;
    }
    if (url.searchParams.get('view') === intendedView) {
      if (sessionStorage) sessionStorage.removeItem('wuffesFeedbackReturnView');
      return;
    }

    url.searchParams.set('view', intendedView);
    try {
      if (sessionStorage) sessionStorage.removeItem('wuffesFeedbackReturnView');
    } catch (e) {}

    window.location.replace(url.pathname + '?' + url.searchParams.toString() + (url.hash || ''));
  } catch (e) {}
})();

function getFocusableElements(container) {
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}

document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
  summary.setAttribute('role', 'button');
  summary.setAttribute('aria-expanded', summary.parentNode.hasAttribute('open'));

  if (summary.nextElementSibling.getAttribute('id')) {
    summary.setAttribute('aria-controls', summary.nextElementSibling.id);
  }

  summary.addEventListener('click', (event) => {
    event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
  });

  if (summary.closest('header-drawer, menu-drawer')) return;
  summary.parentElement.addEventListener('keyup', onKeyUpEscape);
});

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  var elements = getFocusableElements(container);
  var first = elements[0];
  var last = elements[elements.length - 1];

  removeTrapFocus();

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.style.display = 'none';
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.style.display = '';
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );

    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));

    this.querySelector('.menu-drawer-close').addEventListener('click', (event) => {
      event.preventDefault();

      const summaryElement = this.querySelector('summary');
      this.closeMenuDrawer(event, summaryElement);
    });
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      const submenuContainer = summaryElement.nextElementSibling;
      const focusableInSubmenu = getFocusableElements(submenuContainer);
      const focusTarget = focusableInSubmenu[0];
      if (focusTarget) {
        trapFocus(submenuContainer, focusTarget);
      }
      submenuContainer.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();

      // Reads before writes: innerHeight forces layout, so read it while styles
      // are clean; the property write goes AFTER open/close so their own reads
      // (e.g. HeaderDrawer's getBoundingClientRect) don't hit an invalidated tree
      const viewportHeight = window.matchMedia('(max-width: 990px)').matches ? window.innerHeight : null;

      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (viewportHeight !== null) {
        document.documentElement.style.setProperty('--viewport-height', `${viewportHeight}px`);
      }
    } else {
      if (isOpen) {
        this.closeSubmenu(detailsElement);
        return;
      }
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);
        parentMenuElement && parentMenuElement.classList.add('submenu-open');
        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.mainDetailsToggle.classList.remove('menu-opening');
    this.mainDetailsToggle.querySelectorAll('details').forEach((details) => {
      details.removeAttribute('open');
      details.classList.remove('menu-opening');
    });
    this.mainDetailsToggle.querySelectorAll('.submenu-open').forEach((submenu) => {
      submenu.classList.remove('submenu-open');
    });
    document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
    removeTrapFocus(elementToFocus);
    this.closeAnimation(this.mainDetailsToggle);

    // if (event instanceof KeyboardEvent) elementToFocus?.setAttribute('aria-expanded', false);
    elementToFocus?.setAttribute('aria-expanded', false);
  }

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    const parentMenuElement = detailsElement.closest('.submenu-open');
    parentMenuElement && parentMenuElement.classList.remove('submenu-open');
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends MenuDrawer {
  constructor() {
    super();
  }

  openMenuDrawer(summaryElement) {
    this.header = this.header || document.querySelector('.section-header');
    this.borderOffset =
      this.borderOffset || this.closest('.header-wrapper').classList.contains('header-wrapper--border-bottom') ? 1 : 0;
    document.documentElement.style.setProperty(
      '--header-bottom-position',
      `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
    );
    this.header.classList.add('menu-open');

    setTimeout(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
    });

    summaryElement.setAttribute('aria-expanded', true);
    window.addEventListener('resize', this.onResize);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  closeMenuDrawer(event, elementToFocus) {
    if (!elementToFocus) return;
    super.closeMenuDrawer(event, elementToFocus);
    this.header.classList.remove('menu-open');
    window.removeEventListener('resize', this.onResize);
  }

  onResize = () => {
    this.header &&
      document.documentElement.style.setProperty(
        '--header-bottom-position',
        `${parseInt(this.header.getBoundingClientRect().bottom - this.borderOffset)}px`
      );
    document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
  };
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    this.querySelector('[id^="ModalClose-"]')?.addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    if (!opener.hasAttribute('data-prevent-scroll') || opener.getAttribute('data-prevent-scroll') !== 'false') {
      document.body.classList.add('overflow-hidden');
    }
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal) modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

class LazyVideo extends HTMLElement {
  constructor() {
    super();
    this.video = this.querySelector('video');
    this.observer = null;
  }

  connectedCallback() {
    this.initLazyLoading();
  }

  initLazyLoading() {
    if (!('IntersectionObserver' in window)) {
      this.loadAndPlayVideo();
      return;
    }

    this.observer = new IntersectionObserver((entries, observer) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.loadAndPlayVideo();
          observer.unobserve(this);
        }
      });
    });

    this.observer.observe(this);
  }

  loadAndPlayVideo() {
    if (this.video.dataset.poster) {
      this.video.poster = this.video.dataset.poster;
    }
    const sources = this.querySelectorAll('source[data-src]');
    sources.forEach((source) => {
      source.src = source.dataset.src;
    });
    this.video.load();
    if (this.video.hasAttribute('autoplay') && this.video.paused) {
      this.video.play().catch(() => {});
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
      this.observer = null;
    }
  }
}

customElements.define('lazy-video', LazyVideo);

window.addEventListener('error', (event) => {
  if (event.message && (
    event.message.includes('MIME type') ||
    event.message.includes('gorgias') ||
    event.message.includes('replace-mailto')
  )) {
    console.warn('Third-party script error (non-blocking):', event.message);
    event.preventDefault();
    return true;
  }
}, false);

class SliderComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    if (!this.slider || !this.nextButton) return;

    this._whenMeasurable().then(() => {
      this.initDynamicElements();
      this._whenImagesLoaded(this.slider).then(() => this.resetPages());
    });
  }

  _whenMeasurable() {
    return new Promise(resolve => {
      const check = () => {
        const anyWidth = Array.from(this.sliderItems).some(el => el.clientWidth > 0);
        if (anyWidth) return resolve();
        this._raf = requestAnimationFrame(check);
      };
      check();
    });
  }

  _whenImagesLoaded(root) {
    const imgs = Array.from(root.querySelectorAll('img'));
    if (!imgs.length) return Promise.resolve();
    return Promise.allSettled(
      imgs.map(img => img.complete ? Promise.resolve() :
        new Promise(res => img.addEventListener('load', res, { once: true })))
    ).then(() => {});
  }

  initDynamicElements() {
    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this));
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter(el => el.clientWidth > 0);

    if (this.sliderItemsToShow.length >= 2) {
      this.sliderItemOffset = this.sliderItemsToShow[1].offsetLeft - this.sliderItemsToShow[0].offsetLeft || 1;
      this.slidesPerPage = Math.max(1, Math.floor(
        (this.slider.clientWidth - this.sliderItemsToShow[0].offsetLeft) / this.sliderItemOffset
      ));
      this.totalPages = Math.max(1, this.sliderItemsToShow.length - this.slidesPerPage + 1);
    } else {
      this.sliderItemOffset = this.slider.clientWidth || 1;
      this.slidesPerPage = 1;
      this.totalPages = Math.max(1, this.sliderItems.length);
    }
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    // This should be refactored as part of https://github.com/Shopify/dawn/issues/2057
    if (!this.slider || !this.nextButton) return;

    const previousPage = this.currentPage;
    this.currentPage = Math.round(this.slider.scrollLeft / this.sliderItemOffset) + 1;

    if (this.currentPageElement && this.pageTotalElement) {
      this.currentPageElement.textContent = this.currentPage;
      this.pageTotalElement.textContent = this.totalPages;
    }

    if (this.currentPage != previousPage) {
      const currentElement = this.sliderItemsToShow && this.sliderItemsToShow[this.currentPage - 1];
      this.dispatchEvent(
        new CustomEvent('slideChanged', {
          detail: {
            currentPage: this.currentPage,
            currentElement: currentElement,
          },
        })
      );
    }

    if (this.enableSliderLooping) return;
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return;

    if (this.isSlideVisible(this.sliderItemsToShow[0]) && this.slider.scrollLeft === 0) {
      this.prevButton.setAttribute('disabled', 'disabled');
    } else {
      this.prevButton.removeAttribute('disabled');
    }

    if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1]) || this.isScrollAtEnd()) {
      this.nextButton.setAttribute('disabled', 'disabled');
    } else {
      this.nextButton.removeAttribute('disabled');
    }
  }

  isScrollAtEnd() {
    // The trailing `:after` spacer on the slider track inflates scrollWidth, so on wide
    // viewports scrollLeft can advance past the last slide's offsetLeft and isSlideVisible()
    // never returns true — leaving the next button enabled with nothing left to scroll.
    // Comparing remaining scroll distance directly is spacer-agnostic and sub-pixel safe.
    const maxScrollLeft = this.slider.scrollWidth - this.slider.clientWidth;
    return this.slider.scrollLeft >= maxScrollLeft - 1;
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.clientWidth + this.slider.scrollLeft - offset;
    return element.offsetLeft + element.clientWidth <= lastVisibleSlide && element.offsetLeft >= this.slider.scrollLeft;
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? this.slider.scrollLeft + step * this.sliderItemOffset
        : this.slider.scrollLeft - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    this.slider.scrollTo({
      left: position,
    });
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
  }

  connectedCallback() {
    super.connectedCallback();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = this.getAttribute('disable-auto-loop') === null;
    this.peekSlide =
      parseInt(getComputedStyle(this.slider).getPropertyValue('--slide-per-view').trim()) ||
      parseInt(this.dataset.peekSlide) ||
      false;

    this.videoControls = this.slider.querySelectorAll('video-control-wrapper');

    if (this.videoControls?.length) {
      this.videoControls.forEach((control) => {
        const videoControl = control.querySelectorAll('.video-control') || null;

        videoControl
          ? videoControl.forEach((control) => {
              control.addEventListener('click', this.onVideoControlClick.bind(this));
            })
          : null;
      });
    }

    if (!this.sliderControlWrapper) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide') || this.querySelector('[id^="Slide-"]');
    if (this.sliderItemsToShow && this.sliderItemsToShow.length > 0) this.currentPage = 1;

      this.announcerBarAnimationDelay = 0;

      this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
      this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
      this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this));
      this.setSlideVisibility();
      this.slideOffset = this.getHiddenDots();
    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();

    this.updateMobileVideoControls();
    this._onResizeHandler = () => this.updateMobileVideoControls();
    window.addEventListener('resize', this._onResizeHandler, { passive: true });
  }

  onVideoControlClick(event) {
    const action = event.target.dataset.action;
    const thisVideoCard = event.target.closest('.slider-card__info--video');

    const video = thisVideoCard.previousElementSibling.querySelector('video');
    const allVideos = this.querySelectorAll('video');

    const hasNotInteracted = !video.hasAttribute('interacted');

    switch (action) {
      case 'unmute':
        video.muted = false;
        event.target.dataset.action = 'mute';
        event.target.previousElementSibling.dataset.action = !video.paused ? 'pause' : null;
        break;
      case 'mute':
        video.muted = true;
        event.target.dataset.action = 'unmute';
        break;
      case 'pause':
        video.pause();
        event.target.dataset.action = 'play';
        break;
      case 'play':
        video.play();
        hasNotInteracted ? (video.currentTime = 0) : null;
        video.muted = false;
        event.target.dataset.action = 'pause';
        event.target.nextElementSibling.dataset.action = 'mute';
        break;
    }

    video.setAttribute('interacted', '');

    [...allVideos]
      .filter((currentVideo) => currentVideo !== video)
      .forEach((currentVideo) => {
        const currentVideoCard = currentVideo.closest('.slider-card__media').nextElementSibling;
        const muteBtn = currentVideo
          .closest('.slider-card__media')
          .nextElementSibling.querySelector('[data-action="mute"]');

        if (action == 'play' || action == 'unmute') {
          currentVideo.muted = true;
          muteBtn ? (muteBtn.dataset.action = 'unmute') : null;
          currentVideoCard.setAttribute('is-playing', false);
        }
      });

    thisVideoCard.setAttribute('is-playing', !video.paused);
  }

  disconnectedCallback() {
    if (this._onResizeHandler) {
      window.removeEventListener('resize', this._onResizeHandler);
      this._onResizeHandler = null;
    }
  }

  updateMobileVideoControls() {
    const isMobile = window.matchMedia('(max-width: 749px)').matches;
    const videos = this.slider ? this.slider.querySelectorAll('video') : [];

    videos.forEach((video) => {
      if (isMobile) {
        video.controls = false;
        video.removeAttribute('controls');
        video.setAttribute('playsinline', 'true');
        video.setAttribute('webkit-playsinline', 'true');
        video.setAttribute('controlsList', 'nodownload noplaybackrate nofullscreen');
        video.setAttribute('disablepictureinpicture', '');
      } else {
        if (video.dataset.showControls === 'true' || video.hasAttribute('data-show-controls')) {
          video.controls = true;
          video.setAttribute('controls', 'controls');
        }
      }

      const ensureNoControls = () => {
        if (isMobile) {
          video.controls = false;
          video.removeAttribute('controls');
        }
      };
      video.removeEventListener('loadedmetadata', ensureNoControls);
      video.addEventListener('loadedmetadata', ensureNoControls, { once: false });
    });
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return;

    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length - this.slideOffset;

    if (!isFirstSlide && !isLastSlide) {
      return;
    }

    if (!this.enableSliderLooping) return;
    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);
  }

  getHiddenDots() {
    let offset = 0;

    if (this.sliderControlButtons) {
      this.sliderControlButtons.forEach((dots) => {
        if (window.getComputedStyle(dots).display === 'none') {
          offset++;
        }
      });
    }

    return offset;
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.enableSliderLooping && this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);

    this.peekSlide ? this.onPeekSlide() : null;
  }

  onPeekSlide() {
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return;
    const allSlides = this.sliderItemsToShow;

    allSlides.forEach((slide) => slide.classList.remove('slide-peeking'));
    allSlides[this.currentPage + this.peekSlide - 1]?.classList.add('slide-peeking');
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length - (this.dataset.slidesPerView - 1)
        ? 0
        : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
  }

  setSlideVisibility(event) {
    if (!this.sliderItemsToShow || !this.sliderItemsToShow.length) return;

    this.sliderItemsToShow.forEach((item, index) => {
      const linkElements = item.querySelectorAll('a');
      if (index === this.currentPage - 1) {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.removeAttribute('tabindex');
          });
        item.setAttribute('aria-hidden', 'false');
        item.removeAttribute('tabindex');
      } else {
        if (linkElements.length)
          linkElements.forEach((button) => {
            button.setAttribute('tabindex', '-1');
          });
        item.setAttribute('aria-hidden', 'true');
        item.setAttribute('tabindex', '-1');
      }
    });
    this.wasClicked = false;
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
        (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);

class VariantSelects extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('change', this.onVariantChange);
  }

  onVariantChange(event) {
    this.updateOptions();
    this.updateMasterId();
    this.updateSelectedSwatchValue(event);
    this.toggleAddButton(true, '', false);
    this.updatePickupAvailability();
    this.removeErrorMessage();
    this.updateVariantStatuses();

    if (!this.currentVariant) {
      this.toggleAddButton(true, '', true);
      this.setUnavailable();
    } else {
      this.updateURL();
      this.updateVariantInput();
      this.renderProductInfo();
      this.updateShareUrl();
    }
  }

  updateOptions() {
    this.options = Array.from(this.querySelectorAll('select, fieldset'), (element) => {
      if (element.tagName === 'SELECT') {
        return element.value;
      }
      if (element.tagName === 'FIELDSET') {
        return Array.from(element.querySelectorAll('input')).find((radio) => radio.checked)?.value;
      }
    });
  }

  updateMasterId() {
    this.currentVariant = this.getVariantData().find((variant) => {
      return !variant.options
        .map((option, index) => {
          return this.options[index] === option;
        })
        .includes(false);
    });
  }

  updateSelectedSwatchValue({ target }) {
    const { name, value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = this.querySelector(`[data-selected-dropdown-swatch="${name}"] > .swatch`);
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const selectedSwatchValue = this.querySelector(`[data-selected-swatch-value="${name}"]`);
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
    }
  }

  updateURL() {
    if (!this.currentVariant || this.dataset.updateUrl === 'false') return;
    window.history.replaceState({}, '', `${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateShareUrl() {
    const shareButton = document.getElementById(`Share-${this.dataset.section}`);
    if (!shareButton || !shareButton.updateUrl) return;
    shareButton.updateUrl(`${window.shopUrl}${this.dataset.url}?variant=${this.currentVariant.id}`);
  }

  updateVariantInput() {
    const productForms = document.querySelectorAll(
      `#product-form-${this.dataset.section}, #product-form-installment-${this.dataset.section}`
    );
    productForms.forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = this.currentVariant.id;
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateVariantStatuses() {
    const selectedOptionOneVariants = this.variantData.filter(
      (variant) => this.querySelector(':checked').value === variant.option1
    );
    const inputWrappers = [...this.querySelectorAll('.product-form__input')];
    inputWrappers.forEach((option, index) => {
      if (index === 0) return;
      const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
      const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
      const availableOptionInputsValue = selectedOptionOneVariants
        .filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected)
        .map((variantOption) => variantOption[`option${index + 1}`]);
      this.setInputAvailability(optionInputs, availableOptionInputsValue);
    });
  }

  setInputAvailability(elementList, availableValuesList) {
    elementList.forEach((element) => {
      const value = element.getAttribute('value');
      const availableElement = availableValuesList.includes(value);

      if (element.tagName === 'INPUT') {
        element.classList.toggle('disabled', !availableElement);
      } else if (element.tagName === 'OPTION') {
        element.innerText = availableElement
          ? value
          : window.variantStrings.unavailable_with_option.replace('[value]', value);
      }
    });
  }

  updatePickupAvailability() {
    const pickUpAvailability = document.querySelector('pickup-availability');
    if (!pickUpAvailability) return;

    if (this.currentVariant && this.currentVariant.available) {
      pickUpAvailability.fetchAvailability(this.currentVariant.id);
    } else {
      pickUpAvailability.removeAttribute('available');
      pickUpAvailability.innerHTML = '';
    }
  }

  removeErrorMessage() {
    const section = this.closest('section');
    if (!section) return;

    const productForm = section.querySelector('product-form');
    if (productForm) productForm.handleErrorMessage();
  }

  updateMedia(html) {
    const mediaGallerySource = document.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);
    const mediaGalleryDestination = html.querySelector(`[id^="MediaGallery-${this.dataset.section}"] ul`);

    const refreshSourceData = () => {
      const mediaGallerySourceItems = Array.from(mediaGallerySource.querySelectorAll('li[data-media-id]'));
      const sourceSet = new Set(mediaGallerySourceItems.map((item) => item.dataset.mediaId));
      const sourceMap = new Map(mediaGallerySourceItems.map((item, index) => [item.dataset.mediaId, { item, index }]));
      return [mediaGallerySourceItems, sourceSet, sourceMap];
    };

    if (mediaGallerySource && mediaGalleryDestination) {
      let [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
      const mediaGalleryDestinationItems = Array.from(mediaGalleryDestination.querySelectorAll('li[data-media-id]'));
      const destinationSet = new Set(mediaGalleryDestinationItems.map(({ dataset }) => dataset.mediaId));
      let shouldRefresh = false;

      // add items from new data not present in DOM
      for (let i = mediaGalleryDestinationItems.length - 1; i >= 0; i--) {
        if (!sourceSet.has(mediaGalleryDestinationItems[i].dataset.mediaId)) {
          mediaGallerySource.prepend(mediaGalleryDestinationItems[i]);
          shouldRefresh = true;
        }
      }

      // remove items from DOM not present in new data
      for (let i = 0; i < mediaGallerySourceItems.length; i++) {
        if (!destinationSet.has(mediaGallerySourceItems[i].dataset.mediaId)) {
          mediaGallerySourceItems[i].remove();
          shouldRefresh = true;
        }
      }

      // refresh
      if (shouldRefresh) [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();

      // if media galleries don't match, sort to match new data order
      mediaGalleryDestinationItems.forEach((destinationItem, destinationIndex) => {
        const sourceData = sourceMap.get(destinationItem.dataset.mediaId);

        if (sourceData && sourceData.index !== destinationIndex) {
          mediaGallerySource.insertBefore(
            sourceData.item,
            mediaGallerySource.querySelector(`li:nth-of-type(${destinationIndex + 1})`)
          );

          // refresh source now that it has been modified
          [mediaGallerySourceItems, sourceSet, sourceMap] = refreshSourceData();
        }
      });
    }

    if (this.currentVariant.featured_media) {
      document
        .querySelector(`[id^="MediaGallery-${this.dataset.section}"]`)
        ?.setActiveMedia?.(`${this.dataset.section}-${this.currentVariant.featured_media.id}`);
    }

    // update media modal
    const modalContent = document.querySelector(`#ProductModal-${this.dataset.section} .product-media-modal__content`);
    const newModalContent = html.querySelector(`product-modal`);
    if (modalContent && newModalContent) modalContent.innerHTML = newModalContent.innerHTML;
  }

  renderProductInfo() {
    const requestedVariantId = this.currentVariant.id;
    const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;

    fetch(
      `${this.dataset.url}?variant=${requestedVariantId}&section_id=${
        this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section
      }`
    )
      .then((response) => response.text())
      .then((responseText) => {
        // prevent unnecessary ui changes from abandoned selections
        if (this.currentVariant.id !== requestedVariantId) return;

        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const destination = document.getElementById(`price-${this.dataset.section}`);
        const source = html.getElementById(
          `price-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuSource = html.getElementById(
          `Sku-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const skuDestination = document.getElementById(`Sku-${this.dataset.section}`);
        const inventorySource = html.getElementById(
          `Inventory-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );
        const inventoryDestination = document.getElementById(`Inventory-${this.dataset.section}`);

        const volumePricingSource = html.getElementById(
          `Volume-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        this.updateMedia(html);

        const pricePerItemDestination = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
        const pricePerItemSource = html.getElementById(
          `Price-Per-Item-${this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section}`
        );

        const volumePricingDestination = document.getElementById(`Volume-${this.dataset.section}`);
        const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);
        const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);

        if (volumeNote) volumeNote.classList.remove('hidden');
        if (volumePricingDestination) volumePricingDestination.classList.remove('hidden');
        if (qtyRules) qtyRules.classList.remove('hidden');

        if (source && destination) destination.innerHTML = source.innerHTML;
        if (inventorySource && inventoryDestination) inventoryDestination.innerHTML = inventorySource.innerHTML;
        if (skuSource && skuDestination) {
          skuDestination.innerHTML = skuSource.innerHTML;
          skuDestination.classList.toggle('hidden', skuSource.classList.contains('hidden'));
        }

        if (volumePricingSource && volumePricingDestination) {
          volumePricingDestination.innerHTML = volumePricingSource.innerHTML;
        }

        if (pricePerItemSource && pricePerItemDestination) {
          pricePerItemDestination.innerHTML = pricePerItemSource.innerHTML;
          pricePerItemDestination.classList.toggle('hidden', pricePerItemSource.classList.contains('hidden'));
        }

        const price = document.getElementById(`price-${this.dataset.section}`);

        if (price) price.classList.remove('hidden');

        if (inventoryDestination) inventoryDestination.classList.toggle('hidden', inventorySource.innerText === '');

        const addButtonUpdated = html.getElementById(`ProductSubmitButton-${sectionId}`);
        this.toggleAddButton(
          addButtonUpdated ? addButtonUpdated.hasAttribute('disabled') : true,
          window.variantStrings.soldOut
        );

        publish(PUB_SUB_EVENTS.variantChange, {
          data: {
            sectionId,
            html,
            variant: this.currentVariant,
          },
        });
      });
  }

  toggleAddButton(disable = true, text, modifyClass = true) {
    const productForm = document.getElementById(`product-form-${this.dataset.section}`);
    if (!productForm) return;
    const addButton = productForm.querySelector('[name="add"]');
    const addButtonText = productForm.querySelector('[name="add"] > span');
    if (!addButton) return;

    if (disable) {
      addButton.setAttribute('disabled', 'disabled');
      if (text) addButtonText.textContent = text;
    } else {
      addButton.removeAttribute('disabled');
      addButtonText.textContent = window.variantStrings.addToCart;
    }

    if (!modifyClass) return;
  }

  setUnavailable() {
    const button = document.getElementById(`product-form-${this.dataset.section}`);
    const addButton = button.querySelector('[name="add"]');
    const addButtonText = button.querySelector('[name="add"] > span');
    const price = document.getElementById(`price-${this.dataset.section}`);
    const inventory = document.getElementById(`Inventory-${this.dataset.section}`);
    const sku = document.getElementById(`Sku-${this.dataset.section}`);
    const pricePerItem = document.getElementById(`Price-Per-Item-${this.dataset.section}`);
    const volumeNote = document.getElementById(`Volume-Note-${this.dataset.section}`);
    const volumeTable = document.getElementById(`Volume-${this.dataset.section}`);
    const qtyRules = document.getElementById(`Quantity-Rules-${this.dataset.section}`);

    if (!addButton) return;
    addButtonText.textContent = window.variantStrings.unavailable;
    if (price) price.classList.add('hidden');
    if (inventory) inventory.classList.add('hidden');
    if (sku) sku.classList.add('hidden');
    if (pricePerItem) pricePerItem.classList.add('hidden');
    if (volumeNote) volumeNote.classList.add('hidden');
    if (volumeTable) volumeTable.classList.add('hidden');
    if (qtyRules) qtyRules.classList.add('hidden');
  }

  getVariantData() {
    this.variantData = this.variantData || JSON.parse(this.querySelector('[type="application/json"]').textContent);
    return this.variantData;
  }
}

customElements.define('variant-selects', VariantSelects);

class ProductRecommendations extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);

      fetch(this.dataset.url)
        .then((response) => response.text())
        .then((text) => {
          const html = document.createElement('div');
          html.innerHTML = text;
          const recommendations = html.querySelector('product-recommendations');

          if (recommendations && recommendations.innerHTML.trim().length) {
            this.innerHTML = recommendations.innerHTML;
          }

          if (!this.querySelector('slideshow-component') && this.classList.contains('complementary-products')) {
            this.remove();
          }

          if (html.querySelector('.grid__item')) {
            this.classList.add('product-recommendations--loaded');
          }
        })
        .catch((e) => {
          console.error(e);
        });
    };

    new IntersectionObserver(handleIntersection.bind(this), { rootMargin: '0px 0px 400px 0px' }).observe(this);
  }
}

customElements.define('product-recommendations', ProductRecommendations);

class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

class TagsFilter extends HTMLElement {
  constructor() {
    super();

    this.querySelector('button').addEventListener('click', this.filterByTags.bind(this));

    const thisEl = this;

    this.querySelectorAll('.filter_item__input').forEach(function (input) {
      input.addEventListener('change', thisEl.getFilters.bind(thisEl));
    });

    this.productCount = this.closest('.collection-hero__inner').querySelector('.collection-hero__filter_count');
    this.filterTrigger = this.querySelector('#filter_trigger');

    thisEl.filterTags = [];
  }

  filterByTags() {
    let visibleProduct = 0,
      selectedTags = this.filterTags;

    const productItems = document.querySelectorAll('.grid__item_product');

    if (selectedTags.length > 0) {
      productItems.forEach((item) => {
        const productTags = item.getAttribute('data-tags').split(',');
        const hasSelectedTag = selectedTags.some((tag) => productTags.includes(tag));

        item.style.display = hasSelectedTag ? 'list-item' : 'none';

        if (hasSelectedTag) {
          visibleProduct++;
        }
      });

      this.productCount.textContent = `Showing all ${visibleProduct} products`;
    } else {
      productItems.forEach((item) => {
        item.style.display = 'list-item';
      });

      this.productCount.textContent = `Showing all ${productItems.length} products`;
    }

    this.filterTrigger.checked = false;
  }

  getFilters() {
    const tagsInput = this.querySelectorAll('.filter_item__input:checked');

    this.filterTags = Array.from(tagsInput, (tag) => tag.value);
  }
}

customElements.define('tags-filter', TagsFilter);

class CollectionTagFilter extends HTMLElement {
  connectedCallback() {
    this.handlers = [];
    // Radio rendered as selected by the server (current_handle == tag).
    // null when no filter is active (we are on the base collection URL).
    this.activeInput = this.querySelector('input[type="radio"][data-tag]:checked');

    this.querySelectorAll('input[type="radio"][data-tag]').forEach((input) => {
      const handler = this.onClick.bind(this, input);
      this.handlers.push({ input, handler });
      // Listen on click, not change: change does not fire when re-clicking an
      // already-selected radio, but we need to catch that to deselect it.
      input.addEventListener('click', handler);
    });

    this.clearButton = this.querySelector('.product-grid_filter-clear');
    if (this.clearButton) {
      this.onClearClick = this.onClear.bind(this);
      this.clearButton.addEventListener('click', this.onClearClick);
    }
  }

  disconnectedCallback() {
    this.handlers.forEach(({ input, handler }) => input.removeEventListener('click', handler));
    this.handlers = [];
    this.clearButton?.removeEventListener('click', this.onClearClick);
  }

  onClear() {
    const collectionUrl = this.clearButton.dataset.collectionUrl;
    if (!collectionUrl) return;
    // Already on the base (unfiltered) URL → nothing to clear.
    if (!this.activeInput) return;
    window.location.href = collectionUrl;
  }

  onClick(input) {
    const tag = input.dataset.tag;
    const collectionUrl = input.dataset.collectionUrl;
    if (!tag || !collectionUrl) return;

    if (input === this.activeInput) {
      // Re-click on the active filter → deselect, navigate to the base URL.
      window.location.href = collectionUrl;
    } else {
      // Select a new filter → navigate to its tag.
      window.location.href = `${collectionUrl}/${tag}`;
    }
  }
}

if (!customElements.get('collection-tag-filter')) {
  customElements.define('collection-tag-filter', CollectionTagFilter);
}

document.addEventListener('DOMContentLoaded', function () {
  function getQueryParams() {
    const params = new URLSearchParams(window.location.search);
    const queryParams = {};
    params.forEach((value, key) => {
      queryParams[key] = value;
    });
    return queryParams;
  }

  function replacePlaceholders(text, queryParams) {
    return text.replace(/\[\[(.*?)\]\]/g, (match, p1) => {
      return queryParams[p1] !== undefined ? queryParams[p1] : match;
    });
  }

  const elements = document.querySelectorAll('[data-contains-query-variables]');

  if (elements && elements.length > 0) {
    const queryParams = getQueryParams();
    elements.forEach((element) => {
      element.innerHTML = replacePlaceholders(element.innerHTML, queryParams);
    });
  }
});

// Add the klaviyoTags to sessionStorage
function collectKlaviyoTagsJSON(name, value) {
  var attrName = name,
    attrValue = value;

  var klaviyoTags = sessionStorage.getItem('klaviyo_tags_array');
  klaviyoTags = klaviyoTags ? JSON.parse(klaviyoTags) : {};

  if (attrName !== undefined && attrValue !== undefined && attrName !== null && attrValue !== null) {
    klaviyoTags[attrName] = attrValue;
    sessionStorage.setItem('klaviyo_tags_array', JSON.stringify(klaviyoTags));
  }
}

// RECHARGE LOGIN PAGE REDIRECT
if (
  document.location.href.indexOf('/tools/recurring/login?redirect=customer_portal%2Foverview') > -1 ||
  document.location.href.indexOf('/tools/recurring/get-subscription-access?passwordless=true') > -1
) {
  document.body.classList.add('recharge-login-passwordless');

  localStorage.removeItem('customer_portal_url');
}

// RECHARGE CUSTOMER PORTAL
if (document.location.href.indexOf('/tools/recurring/portal') > -1) {
  var customerPortalURL = document.location.pathname + document.location.search;
  localStorage.setItem('customer_portal_url', customerPortalURL);
}

// REDIRECT USERS FROM OLD LOGIN TO PASSWORDLESS LOGIN
if (document.location.href.indexOf('/account/login?return_url=%2Faccount') > -1) {
  location.href = 'https://wuffes.com/tools/recurring/login?redirect=customer_portal%2Foverview';
}

// TRY TO GET RECHARGE DASHBOARD LINK FROM LOCAL STORAGE
// Rewrite every ReCharge login link on the page (header + cart drawer + cart page),
// not just the first — querySelector would only deep-link one instance.
document.addEventListener('DOMContentLoaded', function () {
  var customerPortalURLstorage = localStorage.getItem('customer_portal_url');
  if (!customerPortalURLstorage) return;

  document.querySelectorAll('.js__recharge_login_link').forEach(function (rechargeLoginLink) {
    rechargeLoginLink.setAttribute('href', customerPortalURLstorage);
  });
});

class InputGroup extends HTMLElement {
  static validationMessages = {
    badInput: 'Please enter a valid field value',
    patternMismatch: 'Please enter a valid field value',
    rangeOverflow: 'Value is too long',
    rangeUnderflow: 'Value is too short',
    stepMismatch: 'Please enter a valid field value',
    tooLong: 'Value is too long',
    tooShort: 'Value is too short',
    typeMismatch: 'Please enter a valid field value',
    valueMissing: 'This field is required',
  };

  constructor() {
    super();

    this.input = this.querySelector('input') || this.querySelector('select');
    this.type = this.querySelector('input[data-autocomplete]') ? 'autocomplete' : 'default';
    this.errorMessage = this.querySelector('[data-input-error-message]');

    if (this.type === 'autocomplete') {
      this.autocomplete = this.querySelector('[data-autocomplete-list]');
      this.autocompleteButtons = this.autocomplete.querySelectorAll('button');

      const styleSheet = new CSSStyleSheet();
      if (!document.adoptedStyleSheets.includes(styleSheet)) {
        document.adoptedStyleSheets = [...document.adoptedStyleSheets, styleSheet];
      }


      this.autocomplete.addEventListener('click', (event) => {
        const button = event.target.closest('button');
        if (button && button.name && button.value) {
          this.autocompleteButtons.forEach((btn) => btn.classList.remove('active'));
          button.classList.add('active');
          this.changeInputValue(button.value);
          this.errorMessage.textContent = '';
          this.autocomplete.parentElement.classList.remove('input__list-wrapper--visible');
        }
      });

      this.notFoundMessage = this.querySelector('[data-autocomplete-not-found]');

      this.input.addEventListener('input', (event) => {
        this.errorMessage.textContent = '';
        const value = event.target.dataset.autocompleteDefault === 'true' ? '' : event.target.value.toLowerCase();
        let visibleCount = 0;
        this.autocompleteButtons.forEach((button) => {
          const buttonValue = button.value.toLowerCase();
          const buttonValueIncludesInputValue = buttonValue.includes(value);
          if (buttonValueIncludesInputValue && button.classList.contains('hidden')) {
            button.classList.remove('hidden');
          } else if (!buttonValueIncludesInputValue && !button.classList.contains('hidden')) {
            button.classList.add('hidden');
          }
          if (buttonValueIncludesInputValue) visibleCount++;
        });
        if (this.notFoundMessage) {
          this.notFoundMessage.classList.toggle('hidden', visibleCount > 0 || value === '');
        }
      });

      this.input.addEventListener('focus', () => {
        this.autocomplete.parentElement.classList.add('input__list-wrapper--visible');
      });

      this.onDocumentClick = (event) => {
        if (!this.contains(event.target)) {
          this.autocomplete.parentElement.classList.remove('input__list-wrapper--visible');
        }
      };
      document.addEventListener('click', this.onDocumentClick);
    }
  }

  disconnectedCallback() {
    if (this.onDocumentClick) {
      document.removeEventListener('click', this.onDocumentClick);
    }
  }

  validate() {
    if (this.type === 'autocomplete') return this.validateAutocomplete();

    if (this.input.tagName === 'SELECT') {
      const isValid = this.input.value !== '';
      if (isValid) {
        this.errorMessage.textContent = '';
        this.classList.remove('input-group--invalid');
      } else {
        this.errorMessage.textContent = InputGroup.validationMessages.valueMissing;
        this.classList.add('input-group--invalid');
      }
      return isValid;
    }

    const validity = (({
      valueMissing,
      typeMismatch,
      patternMismatch,
      tooLong,
      tooShort,
      rangeUnderflow,
      rangeOverflow,
      stepMismatch,
      badInput,
      customError,
      valid,
    }) => ({
      valueMissing,
      typeMismatch,
      patternMismatch,
      tooLong,
      tooShort,
      rangeUnderflow,
      rangeOverflow,
      stepMismatch,
      badInput,
      customError,
      valid,
    }))(this.input.validity);

    if (validity.valid) {
      this.errorMessage.textContent = '';
      this.classList.remove('input-group--invalid');
      return true;
    }

    for (const [key, value] of Object.entries(validity)) {
      if (value && key !== 'valid') {
        this.errorMessage.textContent = InputGroup.validationMessages[key];
        this.classList.add('input-group--invalid');
        break;
      }
    }

    return false;
  }

  validateAutocomplete() {
    const buttonValues = Array.from(this.autocompleteButtons, (button) => button.value.toLowerCase());
    const isValid = buttonValues.includes(this.input.value.toLowerCase());

    this.autocomplete.parentElement.classList.remove('input__list-wrapper--visible');
    if (!isValid) {
      this.errorMessage.textContent = 'Select value from the list';
      this.classList.add('input-group--invalid');
      return false;
    } else {
      this.errorMessage.textContent = '';
      this.classList.remove('input-group--invalid');
      return true;
    }
  }

  changeInputValue(value) {
    this.input.value = value;
    this.input.dispatchEvent(new Event('change', { bubbles: true }));
    if (this.autocomplete) {
      this.autocomplete.parentElement.blur();
      this.autocompleteButtons.forEach((btn) => {
        btn.classList.toggle('active', btn.value === value);
      });
    }
  }

  setAutocompleteDefaultValue() {
    if (this.autocomplete) {
      this.changeInputValue(this.autocompleteButtons[0].value);
    }
  }
}

customElements.define('input-group', InputGroup);

function visible(element, options = {}) {
  if (!('IntersectionObserver' in window)) return;

  return new Promise((resolve) => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          observer.unobserve(entry.target);
          resolve();
        }
      },
      {
        threshold: 0.1,
        ...options,
      }
    );

    observer.observe(element);
  });
}

async function fetchWithJson(url, options = {}) {
  const response = await fetch(url, options);
  const contentType = response.headers.get('content-type') || '';
  let data = null;

  if (contentType.includes('application/json')) {
    try {
      data = await response.json();
    } catch {
      throw new Error('Invalid response from server. Please try again.');
    }
  }

  return { response, data };
}

function showErrorMessage(doc, selector, message, isSuccess = false) {
  let errorContainer = doc.querySelector(selector);

  if (!errorContainer) {
    errorContainer = doc.createElement('div');
    errorContainer.className = 'offer-15-days-survey__error-message';
    errorContainer.style.cssText = `
      position: fixed;
      top: 5rem;
      left: 50%;
      transform: translateX(-50%);
      background: ${isSuccess ? '#17443d' : '#d92d20'};
      color: white;
      padding: 2rem;
      border-radius: 0.8rem;
      z-index: 10000;
      max-width: 40rem;
      text-align: center;
      box-shadow: 0 0.4rem 1.2rem rgba(0,0,0,0.15);
      font-family: inherit;
      line-height: 1.4;
    `;
    doc.body.appendChild(errorContainer);
  } else {
    errorContainer.style.background = isSuccess ? '#17443d' : '#d92d20';
  }

  let innerHTML = `<div style="margin-bottom: 8px; font-weight: 600;">${message}</div>`;

  if (!isSuccess) {
    innerHTML += `<div style="font-size: 0.9em; opacity: 0.9;">You can try again or contact our support team for assistance.</div>`;
  }

  errorContainer.innerHTML = innerHTML;

  errorContainer.style.display = 'block';

  setTimeout(() => {
    errorContainer.style.display = 'none';
  }, 8000);
}
